import React, { useState, useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { format, parseISO } from "date-fns";
import { generateRandomString } from "../../helpers/header";
import toast, { Toaster } from "react-hot-toast";
import PaginatedDeviceTable from "js/UserBoard/EventCalendar/Tab/PaginatedDeviceTable";
import AgoraVideoCall from "js/VideoCall/AgoraVideoCall";
import AgoraRTC from "agora-rtc-sdk-ng";
import PaginatedFamilyMembersTable from "js/UserBoard/EventCalendar/Tab/PaginatedFamilyMembersTable";

import {
    KeyboardVoiceRounded,
    MicOffRounded,
    VideocamRounded,
    VideocamOffRounded,
} from "@mui/icons-material";

import {
    fetchOrganizationEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    linkDevicesToEvent,
    generateShareableLink,
    fetchOrganizationDevices,
    fetchLinkedDevices,
    sendEventEmailInvite, getMediaSignUrl
} from "js/Api/index";

function EventCalendar({ userInfo, org }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [devices, setDevices] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [showEventActions, setShowEventActions] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewVideoTrack, setPreviewVideoTrack] = useState(null);
    const [previewAudioTrack, setPreviewAudioTrack] = useState(null);
    const [isPreviewCameraEnabled, setIsPreviewCameraEnabled] = useState(true);
    const [isPreviewMicrophoneEnabled, setIsPreviewMicrophoneEnabled] =
        useState(true);
    const previewVideoRef = useRef(null);

    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedAudioInput, setSelectedAudioInput] = useState("");
    const [selectedAudioOutput, setSelectedAudioOutput] = useState("");
    const [selectedCamera, setSelectedCamera] = useState("");
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isJoiningEvent, setIsJoiningEvent] = useState(false);
    const [selectedGuestTab, setSelectedGuestTab] = useState("CareWindowUsers");

    const [familyMembers, setFamilyMembers] = useState([]);
    const [selectedFamilyMembers, setSelectedFamilyMembers] = useState([]);
    const [filterByInvitedUsers, setFilterByInvitedUsers] = useState(true);
    const [isLoadingFamilyMembers, setIsLoadingFamilyMembers] = useState(false);
    const [showSaveRequiredModal, setShowSaveRequiredModal] = useState(false);
    const [linkExpiry, setLinkExpiry] = useState("24");
    const [shareableLink, setShareableLink] = useState("");
    const [shareableLinkLoading, setShareableLinkLoading] = useState(false);

    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [eventHasTimeChange, setEventHasTimeChange] = useState(false);
    const [originalEventData, setOriginalEventData] = useState(null);
    const [notifyEventId, setNotifyEventId] = useState(null);

    const CAREWINDOW_DEVICE_TYPES = [
        "android-carewindow",
        "android-carewindow-rom",
    ];

    // Family members related
    useEffect(() => {
        if (selectedGuestTab === "FamilyMembers") {
            if (selectedDevices.length === 0 && filterByInvitedUsers) {
                setFamilyMembers([]);
            } else {
                fetchFamilyMembersData();
            }
        }
    }, [selectedGuestTab, selectedDevices, filterByInvitedUsers]);

    useEffect(() => {
        if (!showModal) {
            setFamilyMembers([]);
            setSelectedFamilyMembers([]);
            setShareableLink("");

            setEventHasTimeChange(false);
            setOriginalEventData(null);
        }
    }, [showModal]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (org?.id) {
            fetchEventsData();
        }
    }, [org]);

    useEffect(() => {
        if (org?.code) {
            fetchOrgDevicesData();
        }
    }, [org]);

    const handleTimeChange = (e, field) => {
        try {
            const { value } = e.target;
            if (!value) return;

            const [hours, minutes] = value.split(":").map(Number);
            const currentDate =
                field === "startTime"
                    ? new Date(currentEvent.start)
                    : new Date(currentEvent.end);

            currentDate.setHours(hours, minutes);

            // If this is an existing event, check if time has changed
            if (currentEvent.id && originalEventData) {
                const originalDate =
                    field === "startTime"
                        ? new Date(originalEventData.start)
                        : new Date(originalEventData.end);

                if (currentDate.getTime() !== originalDate.getTime()) {
                    setEventHasTimeChange(true);
                }
            }

            if (field === "startTime") {
                const endDate = new Date(currentDate.getTime() + 60 * 60 * 1000);
                setCurrentEvent((prev) => ({
                    ...prev,
                    start: currentDate,
                    end: endDate,
                }));
            } else {
                setCurrentEvent((prev) => ({
                    ...prev,
                    end: currentDate,
                }));
            }
        } catch (error) {
            console.error("Error handling time change:", error);
        }
    };

    // Handle date change for checking if event time has changed
    const handleDateChange = (e) => {
        const { value } = e.target;
        if (!value) return;

        try {
            const newDate = new Date(value);
            const prevDate = currentEvent?.start
                ? new Date(currentEvent.start)
                : new Date();

            // Validate if newDate is valid
            if (isNaN(newDate.getTime())) {
                console.error("Invalid date value");
                return;
            }

            // Set hours and minutes from previous date
            newDate.setHours(prevDate.getHours(), prevDate.getMinutes());

            // If this is an existing event, check if date has changed
            if (currentEvent.id && originalEventData) {
                const originalDate = new Date(originalEventData.start);
                // Compare only year, month, day
                if (
                    newDate.getFullYear() !== originalDate.getFullYear() ||
                    newDate.getMonth() !== originalDate.getMonth() ||
                    newDate.getDate() !== originalDate.getDate()
                ) {
                    setEventHasTimeChange(true);
                }
            }

            setCurrentEvent((prev) => ({
                ...prev,
                start: newDate,
                end: new Date(newDate.getTime() + 60 * 60 * 1000),
            }));
        } catch (error) {
            console.error("Error handling date change:", error);
        }
    };

    const fetchFamilyMembersData = async (filterState = filterByInvitedUsers) => {
        // If we're filtering by invited users and no users are selected, show nothing
        if (filterState && selectedDevices.length === 0) {
            setFamilyMembers([]);
            return;
        }

        setIsLoadingFamilyMembers(true);

        try {
            let allFamilyMembers = [];

            // If filtering by invited users, only fetch family members for selected devices
            if (filterState) {
                // Fetch family members for each selected device
                for (const deviceId of selectedDevices) {
                    const response = await fetchLinkedDevices(deviceId);

                    if (response.success) {
                        // Get parent device info from the devices array
                        const parentDevice = devices.find((d) => d.id === deviceId);

                        // Map the linked devices to include the parent device information
                        const linkedDevicesWithParent = response.data
                            .filter((device) => device.id !== deviceId) // Filter out the parent device itself
                            .map((device) => ({
                                ...device,
                                parentDeviceName: parentDevice?.name || "Unknown CareWindow",
                            }));

                        allFamilyMembers = [
                            ...allFamilyMembers,
                            ...linkedDevicesWithParent,
                        ];
                    } else {
                        console.error(
                            `Failed to fetch linked devices for ${deviceId}:`,
                            response.error
                        );
                    }
                }
            } else {
                // If not filtering, fetch family members for all devices
                for (const device of devices) {
                    const response = await fetchLinkedDevices(device.id);

                    if (response.success) {
                        const linkedDevicesWithParent = response.data
                            .filter((linkedDevice) => linkedDevice.id !== device.id)
                            .map((linkedDevice) => ({
                                ...linkedDevice,
                                parentDeviceName: device.name,
                            }));

                        allFamilyMembers = [
                            ...allFamilyMembers,
                            ...linkedDevicesWithParent,
                        ];
                    } else {
                        console.error(
                            `Failed to fetch linked devices for ${device.id}:`,
                            response.error
                        );
                    }
                }
            }

            // Remove duplicates by ID
            const uniqueFamilyMembers = allFamilyMembers.filter(
                (member, index, self) =>
                    index === self.findIndex((m) => m.id === member.id)
            );

            setFamilyMembers(uniqueFamilyMembers);
        } catch (error) {
            console.error("Error fetching family members:", error);
        } finally {
            setIsLoadingFamilyMembers(false);
        }
    };

    const handleFamilyMemberSelect = (memberId) => {
        setSelectedFamilyMembers((prev) => {
            if (prev.includes(memberId)) {
                return prev.filter((id) => id !== memberId);
            } else {
                return [...prev, memberId];
            }
        });
    };

    const handleFilterToggle = () => {
        // First update the filter state
        setFilterByInvitedUsers((prevState) => !prevState);

        // Then fetch family members based on the NEW filter state
        // (using the callback form of setState to ensure we have the latest state)
        setFilterByInvitedUsers((newFilterState) => {
            // Use setTimeout to ensure this runs after state is updated
            setTimeout(() => {
                fetchFamilyMembersData(newFilterState);
            }, 0);
            return newFilterState;
        });
    };

    const fetchOrgDevicesData = async () => {
        try {
            const response = await fetchOrganizationDevices(org.code, false);
            if (response.success) {
                setDevices(response.data);
            } else {
                console.error("Failed to fetch devices:", response.error);
            }
        } catch (error) {
            console.error("Error fetching devices:", error);
        }
    };

    const getEventStatus = (eventStart, eventEnd, autoStart) => {
        const now = new Date();
        const startTime = new Date(eventStart);
        const endTime = new Date(eventEnd);

        if (now < startTime) {
            return "upcoming";
        } else if (now >= startTime && now <= endTime) {
            return autoStart ? "inProgress" : "upcoming";
        } else {
            return "ended";
        }
    };

    const fetchEventsData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchOrganizationEvents(org.id);

            if (!response.success) {
                throw new Error(response.error || "Failed to fetch events");
            }

            const data = response.data;
            const formattedEvents = data.map((item) => {
                const startTime = new Date(`${item.e.startTime}Z`);
                const endTime = new Date(`${item.e.endTime}Z`);
                const status = getEventStatus(startTime, endTime, item.e.autoStart);

                let backgroundColor;
                switch (status) {
                    case "upcoming":
                        backgroundColor = "#1976D2";
                        break;
                    case "inProgress":
                        backgroundColor = "#DC3545";
                        break;
                    case "ended":
                        backgroundColor = "#212529";
                        break;
                    default:
                        backgroundColor = "#1976D2";
                }

                // Separate CareWindow devices and family members based on deviceType
                const careWindowDevices = [];
                const eventFamilyMembers = [];

                if (item.device && item.device.length > 0) {
                    item.device.forEach((device) => {
                        if (CAREWINDOW_DEVICE_TYPES.includes(device.deviceType)) {
                            careWindowDevices.push(device);
                        } else {
                            eventFamilyMembers.push(device);
                        }
                    });
                }

                return {
                    id: item.e.id,
                    title: item.e.eventName,
                    start: startTime,
                    end: endTime,
                    autoStart: item.e.autoStart,
                    description: item.e.eventDesc,
                    selectedDevices: careWindowDevices,
                    selectedFamilyMembers: eventFamilyMembers,
                    backgroundColor,
                    recordingFile: item.e.recordingFile
                };
            });

            setEvents(formattedEvents);
        } catch (error) {
            console.error("Error fetching events:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDateSelect = (selectInfo) => {
        const startDate = selectInfo.start;
        setCurrentEvent({
            start: startDate,
            end: startDate,
            title: "",
            extendedProps: {
                description: "",
                location: "",
            },
        });
        setShowModal(true);
    };

    const handleEventClick = (clickInfo) => {
        clickInfo.jsEvent.preventDefault();
        const eventId = clickInfo.event.id;
        const eventStart = new Date(clickInfo.event.start);
        const eventEnd = new Date(clickInfo.event.end);
        const eventData = events.find((e) => e.id === eventId);
        const status = getEventStatus(eventStart, eventEnd, eventData?.autoStart);

        setSelectedEventId(eventId);

        // Calculate position for dropdown
        const rect = clickInfo.el.getBoundingClientRect();
        setDropdownPosition({
            x: rect.left,
            y: rect.bottom,
        });

        setIsJoiningEvent(status === "inProgress");
        setShowDropdown(true);
    };

    const handleEditEvent = (eventId) => {
        setShareableLink("");
        const eventData = events.find((event) => event.id === eventId);
        if (eventData) {
            const deviceIds = eventData.selectedDevices.map((device) => device.id);
            setSelectedDevices(deviceIds);

            // Set selected family members
            if (
                eventData.selectedFamilyMembers &&
                eventData.selectedFamilyMembers.length > 0
            ) {
                const familyMemberIds = eventData.selectedFamilyMembers.map(
                    (member) => member.id
                );
                setSelectedFamilyMembers(familyMemberIds);
            } else {
                setSelectedFamilyMembers([]);
            }

            setOriginalEventData({ ...eventData });
            setEventHasTimeChange(false);

            setCurrentEvent({
                id: eventData.id,
                start: eventData.start,
                end: eventData.end,
                title: eventData.title,
                autoStart: eventData.autoStart,
                selectedDevices: eventData.selectedDevices,
                selectedFamilyMembers: eventData.selectedFamilyMembers || [],
            });
            setShowModal(true);
            setSelectedGuestTab("CareWindowUsers");
        }
        setShowDropdown(false);
    };

    const handleDownloadRecording = async (eventId) => {
        let fileName = events.find(a => a.id === eventId).recordingFile
        const response = await getMediaSignUrl(fileName, "video");
        const blob = await response.blob();

        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element and trigger the download
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName; // Use the video key as the default file name
        document.body.appendChild(a);
        a.click();

        // Clean up the temporary URL and element
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setShowDropdown(false);
    };

    const handleStartEvent = async (eventId) => {
        try {
            const event = events.find((e) => e.id === eventId);
            if (!event) {
                throw new Error("Event not found");
            }

            setShowPreviewModal(true);
            setIsPreviewModalOpen(true);
            setIsPreviewCameraEnabled(true);
            setIsPreviewMicrophoneEnabled(true);

            try {
                await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
                const audioDevices = await AgoraRTC.getMicrophones();
                const audioOutput = await AgoraRTC.getPlaybackDevices();
                const cameraDevices = await AgoraRTC.getCameras();

                setAudioInputDevices(filterDevices(audioDevices));
                setAudioOutputDevices(filterDevices(audioOutput));
                setVideoDevices(filterDevices(cameraDevices));

                let audioTrack = await AgoraRTC.createMicrophoneAudioTrack(
                    selectedAudioInput ? { microphoneId: selectedAudioInput } : undefined
                );
                let videoTrack = await AgoraRTC.createCameraVideoTrack(
                    selectedCamera
                        ? {
                            encoderConfig: {
                                width: 640,
                                height: 360,
                                frameRate: 15,
                            },
                            cameraId: selectedCamera,
                        }
                        : undefined
                );

                setPreviewAudioTrack(audioTrack);
                setPreviewVideoTrack(videoTrack);
                if (previewVideoRef.current) {
                    videoTrack.play(previewVideoRef.current);
                }
            } catch (error) {
                console.error("Failed to create preview tracks", error);
                setIsPreviewCameraEnabled(false);
                setIsPreviewMicrophoneEnabled(false);
            }
        } catch (error) {
            console.error("Error starting event:", error);
            toast.error("Failed to start event");
        }
    };

    const cleanupPreviewTracks = async () => {
        if (previewVideoTrack) {
            previewVideoTrack.stop();
            previewVideoTrack.close();
            setPreviewVideoTrack(null);
        }
        if (previewAudioTrack) {
            previewAudioTrack.stop();
            previewAudioTrack.close();
            setPreviewAudioTrack(null);
        }
    };

    const handleStartCall = async () => {
        setShowPreviewModal(false);
        await cleanupPreviewTracks();

        const callData = {
            userInfo,
            eventId: selectedEventId,
            callerName: userInfo?.name || "Admin",
            initialCameraEnabled: isPreviewCameraEnabled,
            initialMicrophoneEnabled: isPreviewMicrophoneEnabled,
            initialAudio: selectedAudioInput,
            initialVideo: selectedCamera,
            initialAudioOutput: selectedAudioOutput,
            isJoiningEvent: isJoiningEvent,
        };

        const encodedCallData = btoa(JSON.stringify(callData));
        const videoCallTab = window.open(
            `/video-call?data=${encodedCallData}&type=event`,
            "_blank"
        );

        if (videoCallTab) {
            videoCallTab.focus();
        } else {
            toast.error(
                "Unable to open video call tab. Please check your popup blocker settings."
            );
        }
    };

    const hanleClearModal = () => {
        setCurrentEvent(null);
        setSelectedDevices([]);
        setSelectedFamilyMembers([]);
        setShareableLink("");
        setSelectedGuestTab("CareWindowUsers");
        setSearchQuery("");
        setFilterByInvitedUsers(true);
        setEventHasTimeChange(false);
        setOriginalEventData(null);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setCurrentEvent(null);
        setSelectedDevices([]);
        setSelectedFamilyMembers([]);
        setShareableLink("");
        setSelectedGuestTab("CareWindowUsers");
        setSearchQuery("");
        setFilterByInvitedUsers(true);
        setEventHasTimeChange(false);
        setOriginalEventData(null);
    };

    // Combine both CareWindow devices and family members for linkDevicesToEvent
    const linkAllParticipantsToEvent = async (eventId) => {
        try {
            const allParticipantIds = [...selectedDevices, ...selectedFamilyMembers];

            if (allParticipantIds.length === 0) {
                return true; // No participants to link
            }

            const linkResponse = await linkDevicesToEvent(eventId, allParticipantIds);

            if (!linkResponse.success) {
                console.error(
                    "Failed to link participants to event:",
                    linkResponse.error
                );
                toast.error("Failed to link some participants to the event");
                return false;
            }

            return true;
        } catch (error) {
            console.error("Error linking participants to event:", error);
            return false;
        }
    };

    const sendEmailInvites = async (eventId) => {
        try {
            const response = await sendEventEmailInvite(eventId);

            if (response.success) {
                toast.success("Email invitations sent successfully!");
                return true;
            } else {
                toast.error(response.error || "Failed to send email invitations");
                return false;
            }
        } catch (error) {
            console.error("Error sending email invitations:", error);
            toast.error("Failed to send email invitations");
            return false;
        }
    };

    // Check if participants have changed
    const haveParticipantsChanged = () => {
        if (!originalEventData) return false;

        // Check if CareWindow devices changed
        const originalDeviceIds = originalEventData.selectedDevices.map(
            (device) => device.id
        );
        const currentDeviceIds = selectedDevices;

        if (originalDeviceIds.length !== currentDeviceIds.length) return true;

        const devicesDiff =
            originalDeviceIds.some((id) => !currentDeviceIds.includes(id)) ||
            currentDeviceIds.some((id) => !originalDeviceIds.includes(id));

        // Check if family members changed
        const originalFamilyMemberIds = originalEventData.selectedFamilyMembers.map(
            (member) => member.id
        );

        if (originalFamilyMemberIds.length !== selectedFamilyMembers.length)
            return true;

        const familyMembersDiff =
            originalFamilyMemberIds.some(
                (id) => !selectedFamilyMembers.includes(id)
            ) ||
            selectedFamilyMembers.some((id) => !originalFamilyMemberIds.includes(id));

        return devicesDiff || familyMembersDiff;
    };

    // Check if event title has changed
    const hasTitleChanged = () => {
        if (!originalEventData) return false;
        return originalEventData.title !== currentEvent.title;
    };

    const handleEventSubmit = async (e) => {
        e.preventDefault();

        try {
            let eventResponse;
            let eventId;

            const startTimeStr = new Date(currentEvent.start)
                .toISOString()
                .slice(0, -8);
            const endTimeStr = new Date(currentEvent.end).toISOString().slice(0, -8);

            if (currentEvent.id) {
                // Update existing event
                const updateData = {
                    id: currentEvent.id,
                    userId: userInfo.userId,
                    organizationId: org.id,
                    eventName: currentEvent.title,
                    eventDesc: currentEvent.title,
                    createdAt:
                        currentEvent.createdAt || new Date().toISOString().slice(0, -1),
                    updatedAt: new Date().toISOString().slice(0, -1),
                    deletedAt: null,
                    startTime: startTimeStr,
                    endTime: endTimeStr,
                    autoStart: currentEvent.autoStart || false,
                };

                const updateResponse = await updateEvent(updateData);

                if (!updateResponse.success) {
                    throw new Error(updateResponse.error || "Failed to update event");
                }

                eventId = currentEvent.id;

                // Link participants to the event
                const linkSuccess = await linkAllParticipantsToEvent(eventId);

                if (!linkSuccess) {
                    throw new Error("Failed to link participants to the event");
                }

                setShowModal(false);

                // if (eventHasTimeChange) {
                //   await sendEmailInvites(eventId);
                //   toast.success("Event updated and time change notifications sent!");
                // } else if (hasTitleChanged() || haveParticipantsChanged()) {
                //   setNotifyEventId(eventId);
                //   setShowNotifyModal(true);
                // } else {
                //   toast.success("Event successfully updated!");
                // }
                toast.success("Event successfully updated!");
            } else {
                // Create new event
                const createData = {
                    userId: userInfo.userId,
                    organizationId: org.id,
                    eventName: currentEvent.title,
                    eventDesc: currentEvent.title,
                    createdAt: new Date().toISOString().slice(0, -1),
                    updatedAt: new Date().toISOString().slice(0, -1),
                    deletedAt: null,
                    startTime: startTimeStr,
                    endTime: endTimeStr,
                    autoStart: currentEvent.autoStart || false,
                };

                const createResponse = await createEvent(createData);

                if (!createResponse.success) {
                    throw new Error(createResponse.error || "Failed to create event");
                }

                eventId = createResponse.data.id;

                // Link participants to the event
                const linkSuccess = await linkAllParticipantsToEvent(eventId);

                if (!linkSuccess) {
                    throw new Error("Failed to link participants to the event");
                }
                setShowModal(false);

                // Automatically send email notifications for new events
                // await sendEmailInvites(eventId);
                toast.success("Event successfully created!");
                // toast.success("Event successfully created with email notifications!");
            }

            // Refresh events list
            await fetchEventsData();
            if (!showNotifyModal) {
                hanleClearModal();
            }
        } catch (error) {
            console.error("Error saving event:", error);
            setError(error.message);
            toast.error(error.message || "Failed to save event");
        }
    };

    const handleNotifyDecision = async (shouldNotify) => {
        try {
            if (shouldNotify && notifyEventId) {
                await sendEmailInvites(notifyEventId);
                toast.success("Event updated and notifications sent!");
            } else {
                toast.success("Event updated without sending notifications.");
            }

            setShowNotifyModal(false);
            setNotifyEventId(null);
            hanleClearModal();
        } catch (error) {
            console.error("Error handling notification decision:", error);
            toast.error("An error occurred while processing your request.");
            setShowNotifyModal(false);
            setNotifyEventId(null);
            hanleClearModal();
        }
    };

    const handleCreateEventClick = () => {
        setShareableLink("");
        setFilterByInvitedUsers(true);

        const now = new Date();
        const endTime = new Date(now.getTime() + 60 * 60 * 1000);
        setCurrentEvent({
            start: now,
            end: endTime,
            title: "",
            autoStart: false,
            selectedDevices: [],
            selectedFamilyMembers: [],
        });
        setSelectedDevices([]);
        setSelectedFamilyMembers([]);
        setSearchQuery("");
        setSelectedGuestTab("CareWindowUsers");
        setShowModal(true);
    };

    const handleEventInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "title") {
            setCurrentEvent((prev) => ({ ...prev, title: value }));
        } else if (name === "start") {
            // Call the date change handler
            handleDateChange(e);
        }
    };

    const handleDeleteEventAction = async () => {
        if (!currentEvent || !currentEvent.id) return;

        try {
            const response = await deleteEvent(currentEvent.id);

            if (response.success) {
                setEvents((prevEvents) =>
                    prevEvents.filter((event) => event.id !== currentEvent.id)
                );
                handleModalClose();
                toast.success("Event deleted successfully!");
            } else {
                toast.error(response.error || "Failed to delete event");
            }
        } catch (error) {
            console.error("Error deleting event:", error);
            toast.error(error.message || "Failed to delete event");
        }
    };

    const handleGuestSearch = (searchTerm) => {
        setSearchQuery(searchTerm || "");
    };

    const handleDeviceSelect = (deviceId) => {
        setSelectedDevices((prev) => {
            const newSelection = prev.includes(deviceId)
                ? prev.filter((id) => id !== deviceId)
                : [...prev, deviceId];
            console.log("New selection:", newSelection);
            return newSelection;
        });
    };

    const filteredDevices = devices.filter((device) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase().trim();
        return (
            device.name?.toLowerCase().includes(query) ||
            false ||
            device.serialNo?.toLowerCase().includes(query) ||
            false
        );
    });

    const eventDidMount = (info) => {
        // 添加鼠标悬停效果
        info.el.style.cursor = "pointer";
        info.el.style.transition = "background-color 0.2s ease";

        // 添加悬停效果的事件监听器
        const addHoverEffect = () => {
            info.el.style.backgroundColor = "#f0f0f0";
            info.el.style.transform = "scale(1.02)";
        };

        const removeHoverEffect = () => {
            info.el.style.backgroundColor = "";
            info.el.style.transform = "scale(1)";
        };

        info.el.addEventListener("mouseenter", addHoverEffect);
        info.el.addEventListener("mouseleave", removeHoverEffect);
    };

    // 判断事件是否已经开始
    const isEventStarted = (eventStart) => {
        const now = new Date();
        return now > new Date(eventStart);
    };

    const filterDevices = (devices) => {
        return devices
            .filter(
                (device, index, self) =>
                    index === self.findIndex((d) => d.groupId === device.groupId)
            )
            .map((device) => ({
                deviceId: device.deviceId,
                ...device,
                label: device.label
                    .replace(/default|communications/gi, "")
                    .replace(/-/g, "")
                    .trim(),
            }));
    };

    const handleDeviceChange = (event, setter) => {
        setter(event.target.value);
    };

    const togglePreviewCamera = async () => {
        if (previewVideoTrack) {
            await previewVideoTrack.setEnabled(!isPreviewCameraEnabled);
            setIsPreviewCameraEnabled(!isPreviewCameraEnabled);
        }
    };

    const togglePreviewMicrophone = async () => {
        if (previewAudioTrack) {
            await previewAudioTrack.setEnabled(!isPreviewMicrophoneEnabled);
            setIsPreviewMicrophoneEnabled(!isPreviewMicrophoneEnabled);
        }
    };

    const buttonStyle = (enabled) => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "60px",
        height: "60px",
        margin: "0 10px",
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        transition: "all 0.3s ease",
        backgroundColor: enabled ? "#1976D2" : "#1976D2",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        color: "white",
    });

    const handleGuestTabClick = (tabName) => {
        // If trying to access Shareable Link tab in create mode (no event ID), show save required modal
        if (tabName === "ShareableLink" && !currentEvent?.id) {
            setShowSaveRequiredModal(true);
            return;
        }

        setSelectedGuestTab(tabName);

        if (tabName === "ShareableLink" && currentEvent?.id) {
            setShareableLinkLoading(true);

            if (!shareableLink) {
                handleGenerateLink();
            } else {
                setShareableLinkLoading(false);
            }
        }

        if (tabName === "FamilyMembers") {
            fetchFamilyMembersData();
        }
    };

    // Create a separate function for saving from the modal
    const handleSaveFromModal = async () => {
        try {
            // Close the Save Required modal first
            setShowSaveRequiredModal(false);

            // Get formatted times
            const startTimeStr = new Date(currentEvent.start)
                .toISOString()
                .slice(0, -8);
            const endTimeStr = new Date(currentEvent.end).toISOString().slice(0, -8);

            // Create new event
            const createData = {
                userId: userInfo.userId,
                organizationId: org.id,
                eventName: currentEvent.title,
                eventDesc: currentEvent.title,
                createdAt: new Date().toISOString().slice(0, -1),
                updatedAt: new Date().toISOString().slice(0, -1),
                deletedAt: null,
                startTime: startTimeStr,
                endTime: endTimeStr,
                autoStart: currentEvent.autoStart || false,
            };

            setShareableLinkLoading(true);

            const createResponse = await createEvent(createData);

            if (!createResponse.success) {
                throw new Error(createResponse.error || "Failed to create event");
            }

            // Get the ID from the response
            const eventId = createResponse.data.id;

            // Update currentEvent with the new ID
            setCurrentEvent((prev) => ({
                ...prev,
                id: eventId,
            }));

            // Link devices and family members to event
            await linkAllParticipantsToEvent(eventId);

            // Send email invites for the new event
            //await sendEmailInvites(eventId);

            // Show success message
            toast.success("Event saved successfully");

            // Refresh the events list
            await fetchEventsData();

            // Generate shareable link right after saving
            await generateShareableLinkData(eventId);

            // Switch to the Shareable Link tab
            setSelectedGuestTab("ShareableLink");
        } catch (error) {
            console.error("Error saving event from modal:", error);
            toast.error(error.message || "Failed to save event");
        }
    };

    const generateShareableLinkData = async (specificEventId) => {
        try {
            // Use either the provided specific event ID or the current event's ID
            const eventId = specificEventId || currentEvent.id;

            if (!eventId) {
                console.error("No event ID available for generating link");
                setShareableLinkLoading(false);
                return;
            }

            const response = await generateShareableLink(eventId);

            if (response.success) {
                setShareableLink(response.data.url);
                toast.success("Temporary link generated successfully!");
            } else {
                console.error("Failed to generate link:", response.error);
                toast.error("Failed to generate temporary link. Please try again.");
                // Clear the link on error
                setShareableLink("");
            }
        } catch (error) {
            console.error("Error generating temporary link:", error);
            toast.error("An error occurred while generating the temporary link.");
            // Clear the link on error
            setShareableLink("");
        } finally {
            // Always set loading to false when done, whether success or error
            setShareableLinkLoading(false);
        }
    };

    const handleGenerateLink = () => {
        setShareableLinkLoading(true);
        generateShareableLinkData();
    };

    const handleEventContent = (eventInfo) => {
        let current_event = events.find(a => a.id === eventInfo.event.id);
        let hasRecording = current_event.recordingFile !== null && current_event.recordingFile.toString().includes(".mp4") ? true : false;
        return (
            <>
                <b>{format(new Date(current_event.start), "HH:mm") + " " + current_event.title}</b>
                {hasRecording ?
                    <VideocamRounded sx={{ color: "#1976D2", marginLeft: "3px" }} />
                    : null}
            </>
        );
    };


    return (
        <div className="midde_cont">
            <div
                className="container-fluid"
                style={{ paddingLeft: "0", paddingRight: "0" }}
            >
                <div className="row column_title">
                    <div className="col-md-12">
                        <div className="page_title">
                            <h2>Event Calendar</h2>
                        </div>
                    </div>
                </div>
                <div className="row column1">
                    <div className="col-md-12">
                        <div className="white_shd full margin_bottom_30 padding_infor_info">
                            <div style={{ marginBottom: "10px" }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleCreateEventClick}
                                >
                                    + Create Event
                                </button>
                            </div>
                            <FullCalendar
                                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                initialView="dayGridMonth"
                                headerToolbar={{
                                    left: "prev,next today",
                                    center: "title",
                                    right: "dayGridMonth,timeGridWeek,timeGridDay",
                                }}
                                eventContent={handleEventContent}
                                editable={false}
                                selectable={false}
                                // selectMirror={true}
                                // dayMaxEvents={true}
                                events={events}
                                select={handleDateSelect}
                                eventClick={handleEventClick}
                                eventDidMount={eventDidMount}
                            />

                            {showDropdown && (
                                <div
                                    ref={dropdownRef}
                                    style={{
                                        position: "fixed",
                                        left: `${dropdownPosition.x}px`,
                                        top: `${dropdownPosition.y}px`,
                                        zIndex: 1000,
                                        backgroundColor: "white",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                                        padding: "8px 0",
                                    }}
                                >
                                    <div
                                        onClick={() => handleStartEvent(selectedEventId)}
                                        style={{
                                            padding: "8px 16px",
                                            cursor: "pointer",
                                            hover: {
                                                backgroundColor: "#f5f5f5",
                                            },
                                        }}
                                        onMouseOver={(e) =>
                                            (e.target.style.backgroundColor = "#f5f5f5")
                                        }
                                        onMouseOut={(e) =>
                                            (e.target.style.backgroundColor = "white")
                                        }
                                    >
                                        {(() => {
                                            const selectedEvent = events.find(
                                                (event) => event.id === selectedEventId
                                            );
                                            if (selectedEvent) {
                                                const status = getEventStatus(
                                                    selectedEvent.start,
                                                    selectedEvent.end,
                                                    selectedEvent.autoStart
                                                );
                                                if (status === "ended") {
                                                    return "Start Event";
                                                } else if (
                                                    status === "inProgress" &&
                                                    selectedEvent.autoStart
                                                ) {
                                                    return "Join Event";
                                                } else {
                                                    return "Start Event";
                                                }
                                            }
                                            return "Start Event";
                                        })()}
                                    </div>
                                    <div
                                        onClick={() => handleEditEvent(selectedEventId)}
                                        style={{
                                            padding: "8px 16px",
                                            cursor: "pointer",
                                            hover: {
                                                backgroundColor: "#f5f5f5",
                                            },
                                        }}
                                        onMouseOver={(e) =>
                                            (e.target.style.backgroundColor = "#f5f5f5")
                                        }
                                        onMouseOut={(e) =>
                                            (e.target.style.backgroundColor = "white")
                                        }
                                    >
                                        Edit Event
                                    </div>

                                    {events.find(a => a.id === selectedEventId && a.recordingFile !== null && a.recordingFile.includes(".mp4")) !== undefined
                                        &&
                                        <div
                                            onClick={(e) => {
                                                e.preventDefault(); handleDownloadRecording(selectedEventId)
                                            }}
                                            style={{
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                hover: {
                                                    backgroundColor: "#f5f5f5",
                                                },
                                            }}
                                            onMouseOver={(e) =>
                                                (e.target.style.backgroundColor = "#f5f5f5")
                                            }
                                            onMouseOut={(e) =>
                                                (e.target.style.backgroundColor = "white")
                                            }
                                        >
                                            Download Recoding
                                        </div>
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showNotifyModal && (
                <div
                    className="modal"
                    tabIndex="-1"
                    role="dialog"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Notify Participants</h5>
                                <button
                                    type="button"
                                    className="close-button"
                                    onClick={() => handleNotifyDecision(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Would you like to send email notifications to all participants
                                    about the changes to this event?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => handleNotifyDecision(false)}
                                >
                                    No
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleNotifyDecision(true)}
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <div
                    className="modal"
                    tabIndex="-1"
                    role="dialog"
                    style={{
                        display: "block",
                        backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                >
                    <div
                        className="modal-dialog modal-lg"
                        role="document"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "100vh",
                            maxWidth: "900px",
                            margin: "0 auto",
                        }}
                    >
                        <div
                            className="modal-content"
                            style={{
                                width: "750px",
                                maxWidth: "900px",
                                maxHeight: "90vh",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden", // 防止整体溢出
                                padding: "25px"
                            }}
                        >
                            {/* 固定的头部 */}
                            <div className="modal-header" style={{ flexShrink: 0 }}>
                                <h1 className="modal-title">
                                    {currentEvent?.id ? "Edit Event" : "Create Event"}
                                </h1>
                                <button
                                    className="close-button"
                                    style={{ top: "5px" }}
                                    onClick={handleModalClose}
                                >
                                    &times;
                                </button>
                            </div>

                            <div
                                className="modal-body"
                                style={{
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    maxHeight: "calc(90vh - 130px)",
                                    padding: "20px 20px 10px 20px",
                                }}
                            >
                                <form id="eventForm" onSubmit={handleEventSubmit}>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="form-group mb-0" style={{ width: "70%" }}>
                                            <label>Event Name</label>
                                            <input
                                                type="text"
                                                name="title"
                                                className="form-control"
                                                value={currentEvent?.title || ""}
                                                onChange={handleEventInputChange}
                                                required
                                            />
                                        </div>
                                        <div
                                            className="form-group mb-0 text-right"
                                            style={{ width: "30%", paddingTop: "32px" }}
                                        >
                                            <label className="d-flex align-items-center justify-content-end">
                                                <input
                                                    type="checkbox"
                                                    className="mr-2"
                                                    checked={currentEvent?.autoStart || false}
                                                    onChange={(e) =>
                                                        setCurrentEvent((prev) => ({
                                                            ...prev,
                                                            autoStart: e.target.checked,
                                                        }))
                                                    }
                                                />
                                                <span>Automatically start this event</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Event Date</label>
                                                <input
                                                    type="date"
                                                    name="start"
                                                    className="form-control"
                                                    value={
                                                        currentEvent?.start
                                                            ? format(
                                                                new Date(currentEvent.start),
                                                                "yyyy-MM-dd"
                                                            )
                                                            : ""
                                                    }
                                                    onChange={handleEventInputChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Start Time</label>
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    value={
                                                        currentEvent?.start
                                                            ? format(new Date(currentEvent.start), "HH:mm")
                                                            : ""
                                                    }
                                                    onChange={(e) => handleTimeChange(e, "startTime")}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>End Time</label>
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    value={
                                                        currentEvent?.end
                                                            ? format(new Date(currentEvent.end), "HH:mm")
                                                            : ""
                                                    }
                                                    onChange={(e) => handleTimeChange(e, "endTime")}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Guests</label>
                                        <div className="position-relative">
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{ width: "50%" }}
                                                placeholder="Search by CareWindow User or Serial"
                                                value={searchQuery}
                                                onChange={(e) => handleGuestSearch(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Guest Tabs Section - Added for tab navigation similar to user settings */}
                                    <div className="tab_style1">
                                        <div
                                            className="tabbar"
                                            style={{
                                                display: "block",
                                                paddingTop: "10px",
                                                width: "100%",
                                            }}
                                        >
                                            <nav>
                                                <div
                                                    className="nav nav-tabs"
                                                    id="guest-nav-tab"
                                                    role="tablist"
                                                >
                                                    <div
                                                        className={`nav-item nav-link ${selectedGuestTab === "CareWindowUsers"
                                                            ? "active"
                                                            : ""
                                                            }`}
                                                        onClick={() =>
                                                            handleGuestTabClick("CareWindowUsers")
                                                        }
                                                    >
                                                        CareWindow Users
                                                    </div>
                                                    {/* <div
                            className={`nav-item nav-link ${
                              selectedGuestTab === "FamilyMembers"
                                ? "active"
                                : ""
                            }`}
                            onClick={() => handleGuestTabClick("FamilyMembers")}
                          >
                            Family Members
                          </div> */}
                                                    <div
                                                        className={`nav-item nav-link ${selectedGuestTab === "ShareableLink"
                                                            ? "active"
                                                            : ""
                                                            }`}
                                                        onClick={() => handleGuestTabClick("ShareableLink")}
                                                    >
                                                        Shareable Link
                                                    </div>
                                                </div>
                                            </nav>

                                            <div
                                                className="tab-content"
                                                id="guest-nav-tabContent"
                                                style={{
                                                    padding: "0",
                                                    width: "100%",
                                                }}
                                            >
                                                {/* CareWindow Users Tab - Original content */}
                                                <div
                                                    className={`tab-pane fade ${selectedGuestTab === "CareWindowUsers"
                                                        ? "show active"
                                                        : ""
                                                        }`}
                                                    id="guest-carewindow-users"
                                                    role="tabpanel"
                                                >
                                                    <PaginatedDeviceTable
                                                        devices={filteredDevices}
                                                        selectedDevices={selectedDevices}
                                                        onDeviceSelect={handleDeviceSelect}
                                                        itemsPerPage={5}
                                                    />
                                                </div>

                                                {/* Family Members Tab with matching UI style */}
                                                <div
                                                    className={`tab-pane fade ${selectedGuestTab === "FamilyMembers"
                                                        ? "show active"
                                                        : ""
                                                        }`}
                                                    id="guest-family-members"
                                                    role="tabpanel"
                                                >
                                                    <div className="mt-3 mb-3">
                                                        <div className="mb-3 d-flex align-items-center">
                                                            <input
                                                                type="checkbox"
                                                                id="filter-by-invited"
                                                                className="mr-2"
                                                                checked={filterByInvitedUsers}
                                                                onChange={handleFilterToggle}
                                                                style={{ marginRight: "8px" }}
                                                            />
                                                            <label
                                                                htmlFor="filter-by-invited"
                                                                className="mb-0"
                                                            >
                                                                Filter by invited CareWindow Users
                                                            </label>
                                                        </div>

                                                        <PaginatedFamilyMembersTable
                                                            familyMembers={familyMembers}
                                                            selectedFamilyMembers={selectedFamilyMembers}
                                                            onFamilyMemberSelect={handleFamilyMemberSelect}
                                                            isLoading={isLoadingFamilyMembers}
                                                            filterMessage={
                                                                filterByInvitedUsers &&
                                                                    selectedDevices.length === 0
                                                                    ? "Please select CareWindow Users first"
                                                                    : "No family members available"
                                                            }
                                                            itemsPerPage={5}
                                                        />
                                                    </div>
                                                </div>
                                                {/* Shareable Link Tab */}
                                                <div
                                                    className={`tab-pane fade ${selectedGuestTab === "ShareableLink"
                                                        ? "show active"
                                                        : ""
                                                        }`}
                                                    id="guest-shareable-link"
                                                    role="tabpanel"
                                                >
                                                    <div className="mt-3 mb-3">
                                                        <p>
                                                            Invite people outside of the organisation to this
                                                            event by sharing the link.
                                                        </p>

                                                        {shareableLinkLoading ? (
                                                            <div className="text-center py-4">
                                                                <div
                                                                    className="spinner-border text-primary"
                                                                    role="status"
                                                                >
                                                                    <span className="sr-only">Loading...</span>
                                                                </div>
                                                                <p className="mt-2">
                                                                    Generating shareable link...
                                                                </p>
                                                            </div>
                                                        ) : shareableLink ? (
                                                            <div className="form-group mt-4">
                                                                <label
                                                                    htmlFor="shareableLink"
                                                                    style={{
                                                                        display: "block",
                                                                        marginBottom: "10px",
                                                                        fontWeight: "400",
                                                                        color: "black",
                                                                    }}
                                                                >
                                                                    Link
                                                                </label>
                                                                <div
                                                                    className="input-group"
                                                                    style={{ width: "60%" }}
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        id="shareableLink"
                                                                        value={shareableLink}
                                                                        readOnly
                                                                        style={{ marginRight: "10px" }}
                                                                    />
                                                                    <button
                                                                        className="btn btn-outline-dark"
                                                                        type="button"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(
                                                                                shareableLink
                                                                            );
                                                                            toast.success(
                                                                                "Link copied to clipboard!"
                                                                            );
                                                                        }}
                                                                        style={{ width: "100px" }}
                                                                    >
                                                                        Copy
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="alert alert-warning">
                                                                <p>
                                                                    Failed to generate link. Please try again.
                                                                </p>
                                                                <button
                                                                    className="btn btn-outline-primary mt-2"
                                                                    onClick={() => handleGenerateLink()}
                                                                >
                                                                    Retry
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            {/* 固定的页脚 */}
                            <div
                                className="modal-footer"
                                style={{
                                    borderTop: "1px solid #dee2e6",
                                    flexShrink: 0,
                                    padding: "15px 20px",
                                    backgroundColor: "#fff",
                                    borderRadius: "0 0 5px 5px",
                                }}
                            >
                                {currentEvent?.id && (
                                    <button
                                        type="button"
                                        onClick={handleDeleteEventAction}
                                        className="btn btn-danger"
                                        style={{ marginRight: "auto" }}
                                    >
                                        Delete Event
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleModalClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    form="eventForm"
                                    className="btn btn-primary"
                                    disabled={
                                        selectedDevices.length === 0 &&
                                        selectedFamilyMembers.length === 0
                                    }
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSaveRequiredModal && (
                <div
                    className="modal"
                    tabIndex="-1"
                    role="dialog"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Save Required</h5>
                                <button
                                    type="button"
                                    className="close-button"
                                    onClick={() => setShowSaveRequiredModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    {selectedDevices.length === 0 &&
                                        selectedFamilyMembers.length === 0
                                        ? "Please select at least one participant before generating a shareable link."
                                        : "Generating the link requires the event to be saved first."}
                                </p>
                            </div>
                            <div className="modal-footer">
                                {selectedDevices.length === 0 && selectedFamilyMembers.length === 0 ? (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setShowSaveRequiredModal(false)}
                                    >
                                        OK
                                    </button>) : (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowSaveRequiredModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleSaveFromModal}
                                            disabled={selectedDevices.length === 0 &&
                                                selectedFamilyMembers.length === 0}
                                        >
                                            Save
                                        </button>
                                    </>)
                                }
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPreviewModal && (
                <div
                    className="modal fade show"
                    style={{
                        display: "block",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        overflowY: "auto",

                        paddingBottom: "20px",
                    }}
                    aria-modal="true"
                    role="dialog"
                >
                    <div
                        className="modal-dialog modal-lg modal-dialog-centered"
                        role="document"
                        style={{
                            maxWidth: "90%",
                            margin: "auto",
                        }}
                    >
                        <div
                            className="modal-content"
                            style={{
                                backgroundColor: "#f0f0f0",
                                width: "100%",
                                padding: "15px",
                                maxHeight: "90vh",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <div className="modal-header" style={{ flexShrink: 0 }}>
                                <h1 className="modal-title">Group Video Call Preview</h1>
                                <button
                                    type="button"
                                    className="close-button"
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        cleanupPreviewTracks();
                                    }}
                                    aria-label="Close"
                                    style={{
                                        background: "none",
                                        border: "none",
                                        fontSize: "1.5rem",
                                        cursor: "pointer",
                                    }}
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div
                                className="modal-body"
                                style={{
                                    padding: "15px",
                                    overflowY: "auto", // 允许内容滚动
                                    flexGrow: 1,
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        aspectRatio: "16/9",
                                        backgroundColor: "black",
                                        position: "relative",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                    }}
                                >
                                    <div
                                        ref={previewVideoRef}
                                        style={{ width: "100%", height: "100%" }}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: "15px",
                                    }}
                                >
                                    <button
                                        onClick={togglePreviewMicrophone}
                                        style={buttonStyle(isPreviewMicrophoneEnabled)}
                                    >
                                        {isPreviewMicrophoneEnabled ? (
                                            <KeyboardVoiceRounded />
                                        ) : (
                                            <MicOffRounded />
                                        )}
                                    </button>
                                    <button
                                        onClick={togglePreviewCamera}
                                        style={buttonStyle(isPreviewCameraEnabled)}
                                    >
                                        {isPreviewCameraEnabled ? (
                                            <VideocamRounded />
                                        ) : (
                                            <VideocamOffRounded />
                                        )}
                                    </button>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginTop: "15px",
                                    }}
                                >
                                    {/* Microphone Selection */}
                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: "350px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <label
                                            htmlFor="audio-input-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Microphone
                                        </label>
                                        <select
                                            id="audio-input-select"
                                            value={selectedAudioInput}
                                            onChange={(e) =>
                                                handleDeviceChange(e, setSelectedAudioInput)
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default -{" "}
                                                {audioInputDevices[0]?.label || "No Device"}
                                            </option>
                                            {audioInputDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Audio Input"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {/* Speaker Selection */}
                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: "350px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <label
                                            htmlFor="audio-output-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Speaker
                                        </label>
                                        <select
                                            id="audio-output-select"
                                            value={selectedAudioOutput}
                                            onChange={(e) =>
                                                handleDeviceChange(e, setSelectedAudioOutput)
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default -{" "}
                                                {audioOutputDevices[0]?.label || "No Device"}
                                            </option>
                                            {audioOutputDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Audio Output"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {/* Camera Selection */}
                                    <div style={{ width: "100%", maxWidth: "350px" }}>
                                        <label
                                            htmlFor="camera-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Camera
                                        </label>
                                        <select
                                            id="camera-select"
                                            value={selectedCamera}
                                            onChange={(e) => handleDeviceChange(e, setSelectedCamera)}
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default - {videoDevices[0]?.label || "No Device"}
                                            </option>
                                            {videoDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Camera"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="modal-footer"
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    flexShrink: 0,
                                }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        cleanupPreviewTracks();
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleStartCall}
                                >
                                    Start Call
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default EventCalendar;
